// CodeEditor — textarea com overlay de syntax highlighting via Prism.
//
// Implementação layered:
//   <pre> com HTML highlighted (Prism) embaixo
//   <textarea> com texto transparente + caret visível em cima
//
// Os dois precisam ter font/padding/wrapping IDÊNTICOS pra alinhar.
// Estilo "VS Code dark" — inline (não depende de CSS global do Prism).

import { useEffect, useRef } from "preact/hooks";
import Prism from "prismjs";
import "prismjs/components/prism-markup";
import "prismjs/components/prism-css";

interface Props {
  value: string;
  onChange: (v: string) => void;
  language?: "markup" | "css" | "javascript";
  rows?: number;
  disabled?: boolean;
}

export function CodeEditor({
  value,
  onChange,
  language = "markup",
  rows = 22,
  disabled = false
}: Props) {
  const preRef = useRef<HTMLPreElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!preRef.current) return;
    const grammar = Prism.languages[language] || Prism.languages.markup;
    // Final newline trick: garante que o último \n no textarea apareça no pre
    const v = value.endsWith("\n") ? value + " " : value;
    preRef.current.innerHTML = Prism.highlight(v, grammar, language);
  }, [value, language]);

  // Sincroniza scroll do textarea com o pre embaixo
  function onScroll(e: Event) {
    const ta = e.target as HTMLTextAreaElement;
    if (preRef.current) {
      preRef.current.scrollTop = ta.scrollTop;
      preRef.current.scrollLeft = ta.scrollLeft;
    }
  }

  // Tab insere 2 espaços em vez de mudar foco
  function onKeyDown(e: KeyboardEvent) {
    if (e.key === "Tab" && !e.shiftKey) {
      e.preventDefault();
      const ta = taRef.current;
      if (!ta) return;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const next = value.substring(0, start) + "  " + value.substring(end);
      onChange(next);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = start + 2;
      });
    }
  }

  // Estilos compartilhados — TÊM que bater entre pre e textarea
  const shared = {
    margin: "0",
    padding: "12px 14px",
    border: "0",
    fontFamily:
      'ui-monospace, "JetBrains Mono", Menlo, Consolas, "Courier New", monospace',
    fontSize: "12.5px",
    lineHeight: "1.55",
    tabSize: 2,
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
    overflowWrap: "anywhere" as const
  };

  return (
    <div
      class="relative overflow-hidden rounded-md border border-border bg-[#1e1e1e]"
      style={{ minHeight: `${rows * 1.55 * 12.5 + 24}px` }}
    >
      <pre
        ref={preRef}
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 overflow-auto"
        style={{
          ...shared,
          color: "#d4d4d4",
          background: "transparent"
        }}
      />
      <textarea
        ref={taRef}
        value={value}
        rows={rows}
        spellcheck={false}
        disabled={disabled}
        onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
        onScroll={onScroll}
        onKeyDown={onKeyDown}
        class="relative block w-full resize-y outline-none"
        style={{
          ...shared,
          background: "transparent",
          color: "transparent",
          caretColor: "#fff",
          WebkitTextFillColor: "transparent"
        }}
        placeholder="<!doctype html>…"
        title="Editor de HTML"
      />
    </div>
  );
}

// CSS do tema VS Code dark — injetado uma vez no <head>
const VSCODE_DARK_CSS = `
.token.tag, .token.keyword { color: #569cd6; }
.token.attr-name { color: #9cdcfe; }
.token.attr-value, .token.string { color: #ce9178; }
.token.punctuation { color: #d4d4d4; }
.token.comment { color: #6a9955; font-style: italic; }
.token.cdata { color: #6a9955; }
.token.doctype { color: #569cd6; }
.token.entity { color: #4ec9b0; }
.token.style .token.property, .token.property { color: #9cdcfe; }
.token.style .token.string { color: #ce9178; }
.token.namespace { opacity: 0.7; }
.token.function { color: #dcdcaa; }
.token.number { color: #b5cea8; }
.token.operator { color: #d4d4d4; }
.token.url, .token.variable { color: #9cdcfe; }
`;

if (typeof document !== "undefined" && !document.getElementById("vscode-dark-prism")) {
  const style = document.createElement("style");
  style.id = "vscode-dark-prism";
  style.textContent = VSCODE_DARK_CSS;
  document.head.appendChild(style);
}
